import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { ApmTranslatePipe, ResourcesService } from '@mitsubishi/core';
import { ContentResource } from '@mitsubishi/shared/data-access';
import { DialogImage, DialogService, ImageDialogComponent, ImageDialogData } from '@mitsubishi/ui/dialog';

import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

const ROTATE_IMAGE_ID = '11810';

@Component({
  selector: 'apm-resource-type',
  templateUrl: './resource-type.component.html',
  styleUrls: ['./resource-type.component.scss'],
  imports: [ApmTranslatePipe, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceTypeComponent extends FieldType<FieldTypeConfig> implements AfterViewInit {
  readonly imageContainer = viewChild<ElementRef>('imageContainer');
  readonly compareImageContainer = viewChild<ElementRef>('compareImageContainer');

  dialogService = inject(DialogService);
  #resourcesService = inject(ResourcesService);

  rotateResImage = false;
  rotateCompareImage = false;

  readonly defaultResource = signal<ContentResource | undefined>(undefined);
  readonly compareResource = signal<ContentResource | undefined>(undefined);

  ngAfterViewInit(): void {
    if (this.field.defaultValue) {
      const defaultResource = this.#resourcesService.getResource(this.field.defaultValue);
      if (defaultResource && defaultResource.type === 'TEXT') {
        defaultResource['id'] = this.field.defaultValue as number;
      } else if (defaultResource && defaultResource.type === 'IMAGE') {
        this.drawImage(
          defaultResource.value,
          img => this.imageContainer()?.nativeElement.appendChild(img),
          rotate => (this.rotateResImage = rotate)
        );
      }
      this.defaultResource.set(defaultResource);
    }

    const compareValue = this.field.props.compareValue;
    if (compareValue) {
      const compareResource = this.#resourcesService.getResource(compareValue);
      if (compareResource && compareResource.type === 'TEXT') {
        compareResource['id'] = this.field.defaultValue as number;
      } else if (compareResource && compareResource.type === 'IMAGE') {
        this.drawImage(
          compareResource.value,
          img => this.compareImageContainer()?.nativeElement.appendChild(img),
          rotate => (this.rotateCompareImage = rotate)
        );
      }
      this.compareResource.set(compareResource);
    }
  }

  drawImage(src: string, appendChild: (img: HTMLImageElement) => void, setRotateImage: (rotate: boolean) => void) {
    const img = new Image();
    img.src = src;
    img.onload = event => {
      const loadedImage = event.currentTarget as HTMLImageElement;
      const height = loadedImage.naturalHeight;
      const width = loadedImage.naturalWidth;
      img.style.width = '100%';
      const fieldKey = (this.field.key ?? '').toString();
      if (height > width && fieldKey.startsWith(ROTATE_IMAGE_ID)) {
        const scale = width / height;
        img.style.transform = `rotate(90deg) scale(${scale})`;
        setRotateImage(true);
      }
      appendChild(img);
    };
  }

  toggleFullScreen(url: string, compareValueUrl?: string) {
    const images: DialogImage[] = [
      {
        url: url,
        style: this.rotateResImage ? `transform:rotate(90deg)` : '',
      },
    ];

    if (compareValueUrl) {
      images.push({
        url: compareValueUrl,
        style: this.rotateCompareImage ? `transform:rotate(90deg)` : '',
      });
    }

    const data: ImageDialogData = {
      title: this.field.props?.label,
      images: images,
    };
    this.dialogService.openDialogScroll(ImageDialogComponent, 'xl', data);
  }
}
